import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Menu, UserCircle, LogOut, Search, LogIn, UserPlus } from "lucide-react";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { useState, useEffect, FormEvent } from "react";
import { DirectusService } from "@/services/directusService";
import type { DirectusHeaderMenu } from "@/lib/directus";
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Breakpoint strategy (mobile-first):
 *  - < lg (1024px): logo + cart + theme + hamburger. Everything else lives in the Sheet.
 *  - lg → 1360px:    full nav, search collapsed to an icon popover, auth collapsed to an icon menu
 *                   (keeps 1280px laptops from colliding nav ↔ actions).
 *  - ≥ 1360px:      full nav, inline search field, Login/Registar buttons.
 */

/** NOTE: links wrapped in Radix `NavigationMenuLink asChild` must receive a plain
 *  string className — Radix Slot stringifies NavLink's function form, dropping all styles.
 *  Desktop top-level link / trigger. `whitespace-nowrap` keeps multi-word items
 *  (e.g. "Serviço de Ficheiros") on a single line. */
const desktopItemClasses = (isActive = false) =>
  cn(
    "inline-flex h-10 items-center whitespace-nowrap rounded-md px-2.5 xl:px-3 text-sm font-medium transition-colors",
    "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    isActive ? "text-primary" : "text-foreground/90"
  );

/** Mobile rows: min 44px touch target. */
const mobileItemClasses = (isActive = false) =>
  cn(
    "flex min-h-11 w-full items-center rounded-md px-3 text-base font-medium transition-colors",
    "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    isActive ? "text-primary" : "text-foreground"
  );

/**
 * Temporarily disabled top-level nav items. The "Loja" (shop) menu is hidden
 * for now; matches both the Directus-driven item (UUID id, title "Loja") and
 * the hardcoded FALLBACK_MENU entry. Remove from this list to re-enable.
 */
const isLoja = (m: DirectusHeaderMenu) =>
  m.link === '/loja' || m.title?.trim().toLowerCase() === 'loja';

/** Single fallback menu used when Directus data is unavailable */
const FALLBACK_MENU: DirectusHeaderMenu[] = [
  {
    id: 'loja',
    title: 'Loja',
    link: '/loja',
    sub_menu: []
  },
  {
    id: 'servicos',
    title: 'Serviços',
    link: '/servicos',
    sub_menu: [
      { title: 'Reprogramação', link: '/servicos/reprogramacao' },
      { title: 'Diagnóstico', link: '/servicos/diagnostico' },
      { title: 'Reparação', link: '/servicos/reparacao' },
      { title: 'Desbloqueio', link: '/servicos/desbloqueio' },
      { title: 'Clonagem', link: '/servicos/clonagem' },
      { title: 'Airbag', link: '/servicos/airbag' },
      { title: 'AdBlue', link: '/servicos/adblue' },
      { title: 'Chaves', link: '/servicos/chaves' },
      { title: 'Quadrantes', link: '/servicos/quadrantes' }
    ]
  },
  {
    id: 'file-service',
    title: 'Serviço de Ficheiros',
    link: '/file-service',
    sub_menu: []
  },
  {
    id: 'simulador',
    title: 'Simulador',
    link: '/simulador',
    sub_menu: []
  },
  {
    id: 'noticias',
    title: 'Notícias',
    link: '/noticias',
    sub_menu: []
  },
  {
    id: 'contactos',
    title: 'Contactos',
    link: '/contactos',
    sub_menu: []
  }
];

type MenuWithSub = DirectusHeaderMenu & { sub_menu: NonNullable<DirectusHeaderMenu['sub_menu']> };
const hasSubMenu = (m: DirectusHeaderMenu): m is MenuWithSub =>
  Array.isArray(m.sub_menu) && m.sub_menu.length > 0;

const SiteHeader = () => {
  const [logoUrl, setLogoUrl] = useState<string>('/keyprog-logo.png');
  const [headerMenu, setHeaderMenu] = useState<DirectusHeaderMenu[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { isAuthenticated, user, logout } = useUnifiedAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isSectionActive = (link?: string) =>
    !!link && link !== '/' && (pathname === link || pathname.startsWith(`${link}/`));

  const handleSearch = (e?: FormEvent) => {
    e?.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/pesquisa?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
      setSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get header menu from Directus
        const menu = await DirectusService.getHeaderMenu();

        // Use local Keyprog logo
        setLogoUrl('/keyprog-logo.png');

        // Use Directus menu if available, otherwise use fallback.
        // Filter out temporarily-disabled items (e.g. "Loja").
        if (menu && menu.length > 0) {
          setHeaderMenu(menu.filter((m) => !isLoja(m)));
        } else {
          setHeaderMenu(FALLBACK_MENU.filter((m) => !isLoja(m)));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setHeaderMenu(FALLBACK_MENU.filter((m) => !isLoja(m)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const searchField = (id: string, inputClassName?: string) => (
    <form onSubmit={handleSearch} role="search" className="relative flex items-center">
      <label htmlFor={id} className="sr-only">Pesquisar</label>
      <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <Input
        id={id}
        type="search"
        placeholder="Pesquisar..."
        enterKeyHint="search"
        autoComplete="off"
        className={cn("h-10 pl-9", inputClassName)}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
    </form>
  );

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-16 w-full max-w-screen-2xl items-center justify-between gap-3 px-4 md:px-6 lg:gap-4 lg:px-8">
        {/* Brand */}
        <Link
          to="/"
          className="flex min-w-0 flex-shrink-0 items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Keyprog — página inicial"
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="h-9 w-auto object-contain shadow-elev lg:h-10"
              onError={(e) => {
                // If the logo fails to load, just hide the broken image —
                // the "Keyprog" wordmark remains as the brand.
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          {/* Wordmark: hidden only on very narrow phones (< 360px) */}
          <span
            className="hidden min-[360px]:inline-block text-lg xl:text-xl font-semibold leading-[1.15] text-gradient-primary pb-[0.1em] whitespace-nowrap"
            style={{ letterSpacing: '0.05em' }}
          >
            Keyprog
          </span>
        </Link>

        {/* Desktop navigation (≥ lg) */}
        <nav aria-label="Navegação principal" className="hidden min-w-0 flex-1 justify-center lg:flex">
          {!isLoading && (
            <NavigationMenu>
              <NavigationMenuList className="space-x-0 gap-0.5 xl:gap-1">
                {headerMenu.map((menuItem) => (
                  <NavigationMenuItem key={menuItem.id}>
                    {hasSubMenu(menuItem) ? (
                      <>
                        <NavigationMenuTrigger
                          className={cn(
                            "h-10 whitespace-nowrap px-2.5 xl:px-3 bg-transparent",
                            isSectionActive(menuItem.link) ? "text-primary" : "text-foreground/90"
                          )}
                        >
                          {menuItem.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <div className="grid w-[min(92vw,540px)] gap-1 p-3 xl:w-[640px] md:grid-cols-2">
                            {menuItem.link && (
                              <NavigationMenuLink asChild>
                                <NavLink
                                  to={menuItem.link}
                                  className="col-span-full mb-1 rounded-md border-b px-3 pb-2 pt-1 text-sm font-semibold text-primary hover:bg-accent"
                                >
                                  {menuItem.title} — Ver tudo
                                </NavLink>
                              </NavigationMenuLink>
                            )}
                            {menuItem.sub_menu.map((subItem, index) => (
                              <NavigationMenuLink asChild key={`${subItem.link}-${index}`}>
                                <NavLink
                                  to={subItem.link}
                                  className={cn(
                                    "block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                                    pathname === subItem.link && "text-primary font-medium"
                                  )}
                                >
                                  {subItem.title}
                                </NavLink>
                              </NavigationMenuLink>
                            ))}
                          </div>
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <NavigationMenuLink asChild>
                        <NavLink
                          to={menuItem.link || '#'}
                          className={desktopItemClasses(isSectionActive(menuItem.link))}
                        >
                          {menuItem.title}
                        </NavLink>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          )}
        </nav>

        {/* Desktop actions (≥ lg) */}
        <div className="hidden flex-shrink-0 items-center gap-1.5 lg:flex xl:gap-2">
          {/* Inline search on wide screens */}
          <div className="hidden min-[1360px]:block">
            {searchField('header-search-desktop', 'w-48 2xl:w-60')}
          </div>

          {/* Icon search on lg → 1360px */}
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="min-[1360px]:hidden" aria-label="Pesquisar">
                <Search className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-2">
              {searchField('header-search-popover', 'w-full')}
            </PopoverContent>
          </Popover>

          <CartDrawer />
          <ThemeToggle />

          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Conta">
                  <UserCircle className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="max-w-[16rem] truncate">
                  {user.email || 'Cliente'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/perfil" className="flex items-center">
                    <UserCircle className="mr-2 h-4 w-4" />
                    Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              {/* Compact account menu on lg → 1360px */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="min-[1360px]:hidden" aria-label="Entrar ou registar">
                    <UserCircle className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/login" className="flex items-center">
                      <LogIn className="mr-2 h-4 w-4" />
                      Login
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/registo" className="flex items-center">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Registar
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Full buttons on wide screens */}
              <div className="hidden items-center gap-2 min-[1360px]:flex">
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/login">Login</Link>
                </Button>
                <Button size="sm" asChild>
                  <Link to="/registo">Registar</Link>
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Mobile / tablet actions (< lg) */}
        <div className="flex flex-shrink-0 items-center gap-1 lg:hidden">
          <CartDrawer />
          <ThemeToggle />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Abrir menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[88vw] max-w-sm flex-col gap-0 overflow-y-auto p-0 pb-[env(safe-area-inset-bottom)]"
            >
              <div className="flex h-16 shrink-0 items-center border-b px-4 pr-12">
                <SheetTitle className="text-base">Menu</SheetTitle>
              </div>
              <SheetDescription className="sr-only">
                Links de navegação principais e pesquisa do site Keyprog
              </SheetDescription>

              <div className="border-b p-4">
                {searchField('header-search-mobile', 'w-full text-base')}
              </div>

              {/* Close the drawer whenever a navigation link inside it is clicked */}
              <nav
                aria-label="Navegação principal"
                className="flex-1 px-2 py-3"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a')) {
                    setMobileMenuOpen(false);
                  }
                }}
              >
                {!isLoading && (
                  <Accordion type="single" collapsible className="grid gap-0.5">
                    {headerMenu.map((menuItem) =>
                      hasSubMenu(menuItem) ? (
                        <AccordionItem key={menuItem.id} value={String(menuItem.id)} className="border-b-0">
                          <AccordionTrigger
                            className={cn(
                              "min-h-11 rounded-md px-3 py-0 text-base hover:bg-accent hover:no-underline",
                              isSectionActive(menuItem.link) && "text-primary"
                            )}
                          >
                            {menuItem.title}
                          </AccordionTrigger>
                          <AccordionContent className="pb-1 pt-0.5">
                            <div className="ml-3 grid gap-0.5 border-l pl-2">
                              {menuItem.link && (
                                <NavLink
                                  to={menuItem.link}
                                  end
                                  className={({ isActive }) =>
                                    cn(mobileItemClasses(isActive), "min-h-10 text-sm font-semibold text-primary")
                                  }
                                >
                                  Ver tudo
                                </NavLink>
                              )}
                              {menuItem.sub_menu.map((subItem, index) => (
                                <NavLink
                                  key={`${subItem.link}-${index}`}
                                  to={subItem.link}
                                  className={({ isActive }) =>
                                    cn(
                                      mobileItemClasses(isActive),
                                      "min-h-10 text-sm font-normal",
                                      !isActive && "text-muted-foreground"
                                    )
                                  }
                                >
                                  {subItem.title}
                                </NavLink>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ) : (
                        <NavLink
                          key={menuItem.id}
                          to={menuItem.link || '#'}
                          className={({ isActive }) => mobileItemClasses(isActive)}
                        >
                          {menuItem.title}
                        </NavLink>
                      )
                    )}
                  </Accordion>
                )}
              </nav>

              <div className="sticky bottom-0 border-t bg-background p-4">
                {isAuthenticated && user ? (
                  <div className="grid gap-2">
                    <div className="truncate text-sm text-muted-foreground">{user.email}</div>
                    <Button variant="outline" asChild className="h-11 w-full" onClick={() => setMobileMenuOpen(false)}>
                      <Link to="/perfil">
                        <UserCircle className="mr-2 h-4 w-4" />
                        Perfil
                      </Link>
                    </Button>
                    <Button variant="ghost" onClick={handleLogout} className="h-11 w-full">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sair
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" asChild className="h-11" onClick={() => setMobileMenuOpen(false)}>
                      <Link to="/login">Login</Link>
                    </Button>
                    <Button asChild className="h-11" onClick={() => setMobileMenuOpen(false)}>
                      <Link to="/registo">Registar</Link>
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default SiteHeader;
